import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import type { ConnectedService } from "@/types/database";

type Decision = "approve" | "reject" | "revoke";

type Props = {
  open: boolean;
  decision: Decision | null;
  service: ConnectedService | null;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (rejectionReason?: string) => void;
};

export function ApprovalDecisionModal({
  open,
  decision,
  service,
  loading = false,
  onClose,
  onConfirm,
}: Props) {
  const [reason, setReason] = useState("");

  if (!decision || !service) return null;

  const isReject = decision === "reject" || decision === "revoke";
  const title =
    decision === "approve"
      ? "Approve connected service"
      : decision === "revoke"
        ? "Revoke connected service"
        : "Reject connected service";

  const description =
    decision === "approve"
      ? `Approve ${service.businessName || "this business"} for Connected Services? They will gain immediate access in the Shft Control app.`
      : decision === "revoke"
        ? `Revoke Connected Services access for ${service.businessName || "this business"}? They will lose live access until re-approved.`
        : `Reject the Connected Services application from ${service.businessName || "this business"}? Provide a short reason for the record.`;

  function handleClose() {
    setReason("");
    onClose();
  }

  function handleConfirm() {
    if (isReject && !reason.trim()) return;
    onConfirm(isReject ? reason.trim() : undefined);
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant={isReject ? "danger" : "brand"}
            size="sm"
            onClick={handleConfirm}
            disabled={loading || (isReject && !reason.trim())}
          >
            {loading
              ? "Saving…"
              : decision === "approve"
                ? "Approve"
                : decision === "revoke"
                  ? "Revoke"
                  : "Reject"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p>{description}</p>
        {isReject ? (
          <div>
            <label className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-muted">
              Reason
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="field-input"
              placeholder="Explain why this application is being rejected…"
              maxLength={500}
            />
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
